/**
 * Showcase Slide — one slide of a Showcase Slider, as a media/panel pair.
 *
 * Static save: the parent's render.php reaches in and renders each half into
 * its own region, so nothing here describes the frontend layout. The slide's
 * inner-block list is locked to exactly those two halves because render.php
 * addresses them by position.
 */
import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import {
  useBlockProps,
  useInnerBlocksProps,
  InnerBlocks,
  InspectorControls,
} from '@wordpress/block-editor';
import { PanelBody, ColorPalette, BaseControl } from '@wordpress/components';
import { useSettings } from '@wordpress/block-editor';
import metadata from './block.json';
import { SHOWCASE_SLIDE_HALVES } from '../_shared/showcase-slide-defaults';
import {
  fromColorPreset,
  resolvePresetValue,
  toColorPreset,
} from '../../utils/helpers';

function Edit({ attributes, setAttributes }) {
  const { panelColor } = attributes;
  const [palette] = useSettings('color.palette');

  /*
   * The panel colour is painted by the panel on the frontend, which the canvas
   * has no equivalent of — so it is published here as a custom property and
   * editor.scss puts it behind this slide's panel half. Without it every slide
   * reads as plain white and the colour, which is half the composition, is
   * invisible until preview.
   */
  const blockProps = useBlockProps({
    className: 'brandy-showcase-slide',
    style: panelColor
      ? { '--showcase-slide-panel-color': resolvePresetValue(panelColor) }
      : undefined,
  });
  const innerBlocksProps = useInnerBlocksProps(blockProps, {
    template: SHOWCASE_SLIDE_HALVES,
    // Locked as a pair: a slide missing a half would leave one region short
    // and silently misalign every slide after it.
    templateLock: 'all',
  });

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Slide', 'brandy-blocks')} initialOpen={true}>
          <BaseControl
            __nextHasNoMarginBottom
            label={__('Panel colour', 'brandy-blocks')}
            help={__(
              'The panel keeps its box and cross-fades to this colour while this slide is active.',
              'brandy-blocks'
            )}
          >
            {/* Stored as `var:preset|color|<slug>` when the author picks a
                palette swatch, so the panel follows the theme instead of
                freezing today's hex. The control itself only speaks concrete
                colours, hence the conversion either side. */}
            <ColorPalette
              colors={palette || []}
              value={fromColorPreset(panelColor, palette)}
              onChange={(value) =>
                setAttributes({ panelColor: toColorPreset(value, palette) || '' })
              }
              enableAlpha={false}
            />
          </BaseControl>
        </PanelBody>
      </InspectorControls>
      <div {...innerBlocksProps} />
    </>
  );
}

function Save() {
  const blockProps = useBlockProps.save({ className: 'brandy-showcase-slide' });

  return (
    <div {...blockProps}>
      <InnerBlocks.Content />
    </div>
  );
}

registerBlockType(metadata.name, {
  edit: Edit,
  save: Save,
  icon: {
    foreground: '#0061FE',
    src: (
      // A frame split into media and panel — one slide of the pair.
      // Inline fill:none required: `foreground` adds .has-colors whose
      // `fill: currentcolor` overrides the SVG fill attribute.
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        style={{ fill: 'none' }}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="4.5" y="5.5" width="15" height="13" rx="1.5" />
        <path d="M13 5.5v13M15.5 10h2M15.5 13h2" />
      </svg>
    ),
  },
});
